import { createTamagui } from 'tamagui';
import { defaultConfig, createThemes } from '@tamagui/config/v4';

// ─── Type ====================================================
// Space Grotesk everywhere, shared across all SID Technologies products.
//
// Fluid display sizes are NOT in this scale. Tamagui font sizes are numbers,
// so clamp() can't live here; the display steps are CSS custom properties in
// global.css (--type-display, --type-section, ...). This scale covers body,
// labels and UI text, where fixed sizes are correct.

// Satoshi, self-hosted (see public/fonts and styles/statio.css).
const satoshiFont = {
  family: "Satoshi, system-ui, -apple-system, 'Segoe UI', sans-serif",
  face: {
    300: { normal: 'Satoshi' },
    400: { normal: 'Satoshi' },
    500: { normal: 'Satoshi' },
    600: { normal: 'Satoshi' },
    700: { normal: 'Satoshi' },
    800: { normal: 'Satoshi' },
    900: { normal: 'Satoshi' },
  },
  size: {
    1: 11,
    2: 12,
    3: 13,
    4: 14,
    5: 16,
    6: 18,
    7: 20,
    8: 24,
    9: 32,
    10: 48,
    11: 56,
    12: 64,
    13: 80,
    14: 96,
    15: 128,
    true: 16,
  },
  lineHeight: {
    1: 14,
    2: 16,
    3: 18,
    4: 20,
    5: 22,
    6: 26,
    7: 28,
    8: 32,
    9: 40,
    10: 56,
    11: 64,
    12: 72,
    13: 88,
    14: 104,
    15: 140,
    true: 22,
  },
  weight: {
    1: '300',
    2: '400',
    3: '500',
    4: '600',
    5: '700',
    6: '800',
    7: '900',
  },
  letterSpacing: {
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
    6: -0.5,
    7: -0.5,
    8: -0.5,
    9: -1,
    10: -1.5,
    11: -2,
    12: -2,
    13: -2.5,
    14: -3,
    15: -3,
    true: 0,
  },
};

// ─── Radius ==================================================
// Radius is an ARCH property, not a surface property. It belongs on the few
// things that genuinely arch: the mark, the primary action, the one hero
// panel. Everything else is a hairline rule at radius 0. That is why these
// values are generous (8/12/16/20) and used sparingly rather than timid and
// used everywhere.

const radiusTokens = {
  borderRadius: 10,
  borderRadiusSm: 8,
  borderRadiusMd: 10,
  borderRadiusLg: 14,
  borderRadiusXl: 18,
  borderRadius2xl: 24,
  borderRadiusFull: 9999,
};

// Blue-tinted, softer spread: shadows read as depth in water, not drop shadow.
const elevationTokens = {
  elevation0: 'none',
  elevation1: '0 1px 2px rgba(20, 20, 19, 0.08)',
  elevation2: '0 6px 16px -6px rgba(20, 20, 19, 0.18)',
  elevation3: '0 16px 36px -14px rgba(20, 20, 19, 0.26)',
  elevation4: '0 30px 70px -20px rgba(20, 14, 6, 0.4)',
};

const radius = {
  0: 0,
  1: 2,
  2: 4,
  3: 6,
  4: 8,
  5: 10,
  6: 12,
  7: 14,
  8: 16,
  9: 20,
  10: 24,
  11: 28,
  12: 32,
  true: 8,
};

// ─── Palette =================================================
// One palette, the P3 light one. Tamagui builds the dark theme from it by
// reversing the 12 steps, and the colour ramps below are reversed the same way
// in darkExtras. The old hand-written dark theme (navy ground, gold accent) was
// from before P3, was never shown, and is gone.
//
// Step 9 is the solid fill and step 11 is the text, which is the Radix rule and
// also the accessible one. ALLOWED/DENIED as text read $green11/$red11.

const light = {
  // P3 (decided 2026-10-04): cream ground, near-black ink, clay accent. The
  // amber and yellow ramps are CLAY on purpose: interior pages read $amber9 for
  // links and accents, and those are now the clay accent rather than gold.
  background: '#f0eee6',
  backgroundHover: '#e8e4d8',
  backgroundPress: '#e3dacc',
  backgroundFocus: '#e8e4d8',
  backgroundStrong: '#e8e4d8',
  backgroundTransparent: 'rgba(240, 238, 230, 0)',

  color: '#141413',
  colorHover: '#000000',
  colorPress: '#3d3d3a',
  colorFocus: '#000000',
  colorTransparent: 'rgba(20, 20, 19, 0)',

  color1: '#f0eee6',
  color2: '#e8e4d8',
  color3: '#e3dacc',
  color4: '#d9d6cb',
  color5: '#c4c0b4',
  color6: '#a19e94',
  color7: '#7d7b74',
  color8: '#6a6963',
  color9: '#4f4e4a',
  color10: '#3d3d3a',
  color11: '#262624',
  color12: '#141413',

  accentBackground: '#141413',
  accentColor: '#f0eee6',

  borderColor: '#d9d6cb',
  borderColorHover: '#c4c0b4',
  borderColorPress: '#d9d6cb',
  borderColorFocus: '#a34a29',

  shadowColor: 'rgba(20, 20, 19, 0.10)',
  shadowColorHover: 'rgba(20, 20, 19, 0.16)',

  blue1: '#eef3f4',
  blue2: '#dde7e9',
  blue3: '#bccfd3',
  blue4: '#94b0b6',
  blue5: '#6e9199',
  blue6: '#577582',
  blue7: '#4e6b6d',
  blue8: '#3f5f6c',
  blue9: '#3f5f6c',
  blue10: '#344f5a',
  blue11: '#2a4049',
  blue12: '#1a2a30',

  purple1: '#f6f3fa',
  purple2: '#ece6f5',
  purple3: '#d8cceb',
  purple4: '#bca8dc',
  purple5: '#9c80c8',
  purple6: '#7f5cb0',
  purple7: '#634394',
  purple8: '#4a3070',
  purple9: '#634394',
  purple10: '#4a3070',
  purple11: '#382356',
  purple12: '#22143a',

  green1: '#eef8f2',
  green2: '#e4f4ea',
  green3: '#c4e6d0',
  green4: '#97cfaa',
  green5: '#5fae7e',
  green6: '#3a8c5d',
  green7: '#2a7b50',
  green8: '#1f6b45',
  green9: '#1f6b45',
  green10: '#195a3a',
  green11: '#144a30',
  green12: '#0c2e1e',

  red1: '#fdf2f0',
  red2: '#fbe5e1',
  red3: '#f4c4bb',
  red4: '#e89889',
  red5: '#d86a58',
  red6: '#c4473a',
  red7: '#b83426',
  red8: '#ad2b1c',
  red9: '#ad2b1c',
  red10: '#922418',
  red11: '#771d13',
  red12: '#4d120c',

  yellow1: '#f9eee9',
  yellow2: '#f3ddd3',
  yellow3: '#e8bfae',
  yellow4: '#d99a82',
  yellow5: '#c97a5c',
  yellow6: '#b8603f',
  yellow7: '#a34a29',
  yellow8: '#8e3f22',
  yellow9: '#a34a29',
  yellow10: '#8e3f22',
  yellow11: '#74331b',
  yellow12: '#4f2212',

  amber1: '#f9eee9',
  amber2: '#f3ddd3',
  amber3: '#e8bfae',
  amber4: '#d99a82',
  amber5: '#c97a5c',
  amber6: '#b8603f',
  amber7: '#a34a29',
  amber8: '#8e3f22',
  amber9: '#a34a29',
  amber10: '#8e3f22',
  amber11: '#74331b',
  amber12: '#4f2212',

  ...radiusTokens,
  ...elevationTokens,
};

// ─── Theme construction ======================================
// createThemes wants the 12-step palette as an array and everything else as
// string-valued "extra". It drops non-string tokens, which is why radius and
// elevation are re-applied in getTheme, and why global.css ALSO declares the
// radius scale as :root custom properties. Both paths are load-bearing:
// `$borderRadiusMd` style props read the theme, and ~42 call sites read
// var(--radius-md) directly. Removing either one flattens every corner.
//
// The CSS custom properties are named --radius-*, NOT --borderRadius*. Tamagui
// emits the theme's own radius tokens under the --borderRadius* names as
// UNITLESS numbers, at a selector that outranks a plain :root block, so any
// call site reading var(--borderRadiusSm) received `6` rather than `6px` and
// rendered a square corner. Do not rename them back.

const paletteOf = (t: typeof light) => [
  t.color1,
  t.color2,
  t.color3,
  t.color4,
  t.color5,
  t.color6,
  t.color7,
  t.color8,
  t.color9,
  t.color10,
  t.color11,
  t.color12,
];

const PALETTE_KEYS = new Set([
  'color1',
  'color2',
  'color3',
  'color4',
  'color5',
  'color6',
  'color7',
  'color8',
  'color9',
  'color10',
  'color11',
  'color12',
]);

function stringExtras(theme: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(theme)) {
    if (!PALETTE_KEYS.has(k) && typeof v === 'string') out[k] = v;
  }
  return out;
}

// The ramps that have a step 1 to 12, reversed for dark: light step 1 (the
// palest tint) becomes dark step 12. The rest of the dark theme, background,
// color, borders, comes from Tamagui's template over the reversed palette.
const RAMPS = ['blue', 'purple', 'green', 'red', 'yellow', 'amber'] as const;
const darkExtras = Object.fromEntries(
  RAMPS.flatMap((r) =>
    Array.from({ length: 12 }, (_, i) => [
      `${r}${i + 1}`,
      (light as Record<string, unknown>)[`${r}${12 - i}`] as string,
    ]),
  ),
);

const themes = createThemes({
  base: {
    palette: paletteOf(light),
    extra: { dark: darkExtras, light: stringExtras(light) },
  },
  getTheme: ({ theme }) => ({ ...theme, ...radiusTokens, ...elevationTokens }),
});

export const config = createTamagui({
  ...defaultConfig,
  themes,
  fonts: {
    ...defaultConfig.fonts,
    body: satoshiFont,
    heading: satoshiFont,
  },
  radius,
});

export default config;

export type AppConfig = typeof config;

declare module 'tamagui' {
  interface TamaguiCustomConfig extends AppConfig {}
}
